import React, { useEffect, useId, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { AlertTriangle, ClipboardList, ShoppingCart, Zap } from 'lucide-react';
import { useAuth } from '../../hooks/useAuth';
import { API_BASE_URL } from '../config/api';
import { formatMoney } from '../../utils/formatMoney';
import { LoadFailed } from '../settings/instantSettings';
import {
  Column, DataTable, GHOST_BTN, ListPage, ListToolbar, PRIMARY_BTN, Stat, Stats, StatusChip,
} from '../shared/listPage';
import SuppliersManagement from './SuppliersManagement';
import RawMaterialsManagement from './RawMaterialsManagement';
import PurchaseRecording from './PurchaseRecording';
import SimpleIngredientManager from './SimpleIngredientManager';
import StockCountModal from './StockCountModal';
import WarehouseSetupWizard from './WarehouseSetupWizard';
import { BlockTitle, SectionTab, SectionTabs, STOCK_TONES, fmtQty } from './warehouseUi';

/**
 * «Αποθήκη» (Κατάλογος) στη διάταξη «Πίνακας» (14/10/2026): μία σελίδα με καρτέλες — Επισκόπηση · Προμηθευτές ·
 * Πρώτες ύλες · Υλικά / Συνταγές · Αγορές — αντί για τις κάρτες-συνδέσμους με εικονίδιο και το «Πίσω». Η
 * «Επισκόπηση» είναι ο παλιός πίνακας ελέγχου (ίδια 4 αιτήματα, ίδιοι υπολογισμοί, ίδιος οδηγός εγκατάστασης
 * όταν δεν υπάρχει τίποτα), με αριθμούς σε μία γραμμή κειμένου αντί για χρωματιστά κουτιά. Η «Καταγραφή αγοράς»
 * (ως 14/10 στρογγυλό πράσινο κουμπί κάτω δεξιά) είναι στην κεφαλίδα και ανοίγει κατευθείαν τη φόρμα.
 */

type Section = 'dashboard' | 'suppliers' | 'raw-materials' | 'ingredients' | 'purchase';

interface LowStockItem {
  id: number;
  name: string;
  current_stock: number;
  min_stock_level: number;
  purchase_unit: string;
}

interface DashboardStats {
  totalMaterials: number;
  lowStockCount: number;
  outOfStockCount: number;
  totalValue: number;
  totalIngredients: number;
  totalSuppliers: number;
  recentPurchases: number;
  lowStockItems: LowStockItem[];
}

const WarehouseManagement: React.FC = () => {
  const { t } = useTranslation(['warehouse', 'common', 'settings']);
  const { store } = useAuth();
  const uid = useId();
  const [activeSection, setActiveSection] = useState<Section>('dashboard');
  const [stats, setStats] = useState<DashboardStats | null>(null);
  const [loadingStats, setLoadingStats] = useState(true);
  const [statsFailed, setStatsFailed] = useState(false);
  const [showStockCount, setShowStockCount] = useState(false);
  const [showWizard, setShowWizard] = useState(false);
  // Auto-show wizard when store has no raw materials AND no suppliers
  const [wizardDismissed, setWizardDismissed] = useState(false);
  // «Καταγραφή αγοράς» από την κεφαλίδα: οι Αγορές ανοίγουν με τη φόρμα· από την καρτέλα, με το ιστορικό.
  const [purchaseStartNew, setPurchaseStartNew] = useState(false);

  useEffect(() => {
    if (store?.id && activeSection === 'dashboard') {
      fetchDashboardStats();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [store?.id, activeSection]);

  const fetchDashboardStats = async () => {
    setLoadingStats(true);
    setStatsFailed(false);
    try {
      const token = localStorage.getItem('xentrix_token');
      const headers = { 'Authorization': `Bearer ${token}` };

      const [materialsRes, ingredientsRes, suppliersRes, purchasesRes] = await Promise.all([
        fetch(`${API_BASE_URL}/api/warehouse/stores/${store?.id}/raw-materials`, { headers }),
        fetch(`${API_BASE_URL}/api/warehouse/ingredients`, { headers }),
        fetch(`${API_BASE_URL}/api/warehouse/stores/${store?.id}/suppliers`, { headers }),
        fetch(`${API_BASE_URL}/api/warehouse/stores/${store?.id}/purchase-transactions`, { headers })
      ]);

      const materialsData = materialsRes.ok ? await materialsRes.json() : { rawMaterials: [] };
      const ingredientsData = ingredientsRes.ok ? await ingredientsRes.json() : [];
      const suppliersData = suppliersRes.ok ? await suppliersRes.json() : { suppliers: [] };
      const purchasesData = purchasesRes.ok ? await purchasesRes.json() : { transactions: [] };

      const materials = materialsData.rawMaterials || [];
      const lowStock = materials.filter((m: any) => m.current_stock > 0 && m.current_stock <= m.min_stock_level);
      const outOfStock = materials.filter((m: any) => m.current_stock <= 0);
      const totalValue = materials.reduce((sum: number, m: any) => sum + (parseFloat(m.current_stock) * parseFloat(m.purchase_price || 0)), 0);

      const supplierCount = (suppliersData.suppliers || []).length;
      setStats({
        totalMaterials: materials.length,
        lowStockCount: lowStock.length,
        outOfStockCount: outOfStock.length,
        totalValue,
        totalIngredients: Array.isArray(ingredientsData) ? ingredientsData.length : 0,
        totalSuppliers: supplierCount,
        recentPurchases: (purchasesData.transactions || []).length,
        lowStockItems: [...outOfStock, ...lowStock].slice(0, 5).map((m: any) => ({
          id: m.id,
          name: m.name,
          current_stock: parseFloat(m.current_stock),
          min_stock_level: parseFloat(m.min_stock_level),
          purchase_unit: m.purchase_unit
        }))
      });
      // Auto-show wizard if no data at all
      if (materials.length === 0 && supplierCount === 0 && !wizardDismissed) {
        setShowWizard(true);
      }
    } catch (error) {
      console.error('Failed to fetch dashboard stats:', error);
      setStatsFailed(true);
    } finally {
      setLoadingStats(false);
    }
  };

  const goTo = (section: Section) => {
    setPurchaseStartNew(false);
    setActiveSection(section);
  };

  const goToNewPurchase = () => {
    setPurchaseStartNew(true);
    setActiveSection('purchase');
  };

  const tabs: Array<SectionTab<Section>> = [
    { id: 'dashboard', label: t('warehouse:ui.tabs.overview') },
    { id: 'suppliers', label: t('warehouse:ui.tabs.suppliers') },
    { id: 'raw-materials', label: t('warehouse:ui.tabs.rawMaterials') },
    { id: 'ingredients', label: t('warehouse:ui.tabs.recipes') },
    { id: 'purchase', label: t('warehouse:ui.tabs.purchases') },
  ];

  const subtitles: Record<Section, string> = {
    'dashboard': t('warehouse:ui.subtitle.dashboard'),
    'suppliers': t('warehouse:ui.subtitle.suppliers'),
    'raw-materials': t('warehouse:ui.subtitle.rawMaterials'),
    'ingredients': t('warehouse:simpleIngredientDesc'),
    'purchase': t('warehouse:ui.subtitle.purchases'),
  };

  const renderSection = () => {
    switch (activeSection) {
      case 'suppliers':
        return <SuppliersManagement />;
      case 'raw-materials':
        return <RawMaterialsManagement />;
      case 'ingredients':
        return <SimpleIngredientManager />;
      case 'purchase':
        return <PurchaseRecording startWithNew={purchaseStartNew} />;
      default:
        return null;
    }
  };

  const outCount = stats?.outOfStockCount || 0;
  const lowCount = stats?.lowStockCount || 0;

  const attentionColumns: Column<LowStockItem>[] = [
    { key: 'name', header: t('warehouse:ui.overview.material'), width: 'minmax(180px, 2fr)', render: item => <span className="font-medium">{item.name}</span> },
    {
      key: 'stock',
      header: t('warehouse:ui.overview.stock'),
      width: 'minmax(120px, 1fr)',
      align: 'right',
      stack: 'end',
      render: item => (
        <span className={`tabular-nums font-medium ${item.current_stock <= 0 ? 'text-red-700 dark:text-red-400' : 'text-amber-800 dark:text-amber-300'}`}>
          {fmtQty(item.current_stock)} {item.purchase_unit}
        </span>
      ),
      stackRender: item => `${fmtQty(item.current_stock)} ${item.purchase_unit}`,
    },
    {
      key: 'min',
      header: t('warehouse:ui.overview.min'),
      width: 'minmax(110px, 1fr)',
      align: 'right',
      render: item => <span className="tabular-nums text-t3">{fmtQty(item.min_stock_level)}</span>,
      stackRender: item => t('warehouse:ui.minShort', { value: fmtQty(item.min_stock_level) }),
    },
    {
      key: 'state',
      header: t('warehouse:ui.overview.state'),
      width: '150px',
      render: item => (item.current_stock <= 0
        ? <StatusChip tone={STOCK_TONES.out}>{t('warehouse:ui.status.out')}</StatusChip>
        : <StatusChip tone={STOCK_TONES.low}>{t('warehouse:ui.status.low')}</StatusChip>),
    },
  ];

  const renderDashboard = () => {
    if (statsFailed && !loadingStats) {
      return (
        <div className="border-t border-line pt-5">
          <LoadFailed message={t('warehouse:ui.overview.loadFailed')} onRetry={fetchDashboardStats} />
        </div>
      );
    }

    return (
      <>
        {/* Setup Wizard — shown inline at top when active */}
        {showWizard ? (
          <div className="border-t border-line pt-5 pb-2">
            <WarehouseSetupWizard
              onComplete={() => { setShowWizard(false); fetchDashboardStats(); }}
              onDismiss={() => { setShowWizard(false); setWizardDismissed(true); }}
            />
          </div>
        ) : (
          <ListToolbar
            actions={(
              <>
                <button type="button" className={GHOST_BTN} onClick={() => setShowWizard(true)}>
                  <Zap className="w-4 h-4" aria-hidden="true" />
                  {t('warehouse:ui.overview.wizard')}
                </button>
                <button type="button" className={PRIMARY_BTN} onClick={() => setShowStockCount(true)}>
                  <ClipboardList className="w-4 h-4" aria-hidden="true" />
                  {t('warehouse:ui.overview.stockCount')}
                </button>
              </>
            )}
          />
        )}

        {loadingStats ? (
          <div className="space-y-3 py-4" data-testid="warehouse-overview-loading">
            {[48, 66, 38].map((w, i) => <span key={i} className="lp-skel" style={{ width: `${w}%` }} />)}
            <span className="sr-only" role="status">{t('common:loading')}</span>
          </div>
        ) : (
          <div className="space-y-8 pt-2" data-testid="warehouse-overview">
            <div className="space-y-3">
              <Stats testId="warehouse-stats">
                <Stat label={t('warehouse:ui.overview.stockValue')} value={formatMoney(stats?.totalValue || 0)} />
                <Stat label={t('warehouse:ui.overview.rawMaterials')} value={stats?.totalMaterials || 0} />
                <Stat label={t('warehouse:ui.overview.recipes')} value={stats?.totalIngredients || 0} />
                <Stat label={t('warehouse:ui.overview.suppliers')} value={stats?.totalSuppliers || 0} />
              </Stats>
              <p className="flex flex-wrap items-center gap-2 text-sm" data-testid="warehouse-alerts">
                <span className="text-t3">{t('warehouse:ui.overview.alerts')}</span>
                {outCount > 0 && <StatusChip tone="danger">{t('warehouse:exhaustedAlert', { count: outCount })}</StatusChip>}
                {lowCount > 0 && <StatusChip tone="warn">{t('warehouse:lowAlert', { count: lowCount })}</StatusChip>}
                {outCount === 0 && lowCount === 0 && <StatusChip tone="ok">{t('warehouse:ui.overview.allOk')}</StatusChip>}
              </p>
            </div>

            {/* Low Stock Alerts */}
            {stats?.lowStockItems && stats.lowStockItems.length > 0 && (
              <section aria-labelledby={`${uid}-attention`} data-testid="warehouse-attention">
                <div className="flex flex-wrap items-center justify-between gap-3 mb-2">
                  <BlockTitle id={`${uid}-attention`} className="flex items-center gap-2">
                    <AlertTriangle className="w-4 h-4 text-amber-700 dark:text-amber-400" aria-hidden="true" />
                    {t('warehouse:ui.overview.attention')}
                  </BlockTitle>
                  <button type="button" className="xs-link text-sm" onClick={() => goTo('raw-materials')}>
                    {t('warehouse:ui.overview.viewAll')}
                  </button>
                </div>
                <DataTable
                  columns={attentionColumns}
                  rows={stats.lowStockItems}
                  rowKey={item => item.id ?? item.name}
                  ariaLabel={t('warehouse:ui.overview.attention')}
                  dense
                  empty={null}
                  testId="warehouse-attention-table"
                />
              </section>
            )}
          </div>
        )}
      </>
    );
  };

  return (
    <ListPage
      eyebrow={t('settings:nav.groups.catalog')}
      title={t('settings:nav.warehouse')}
      subtitle={subtitles[activeSection]}
      actions={activeSection !== 'purchase' ? (
        <button type="button" className={GHOST_BTN} onClick={goToNewPurchase}>
          <ShoppingCart className="w-4 h-4" aria-hidden="true" />
          {t('warehouse:ui.recordPurchase')}
        </button>
      ) : undefined}
      testId="warehouse-page"
    >
      {/* Stock Count full-screen modal */}
      {showStockCount && (
        <StockCountModal
          onClose={() => setShowStockCount(false)}
          onComplete={() => { setShowStockCount(false); fetchDashboardStats(); }}
        />
      )}

      <SectionTabs tabs={tabs} active={activeSection} onChange={goTo} label={t('warehouse:ui.tabs.label')} idPrefix={uid} />

      <div id={`${uid}-tabpanel`} role="tabpanel" aria-labelledby={`${uid}-tab-${activeSection}`}>
        {activeSection === 'dashboard' ? renderDashboard() : renderSection()}
      </div>
    </ListPage>
  );
};

export default WarehouseManagement;
